import EdgeCrop from "@/components/EdgeCrop";
import { IRectCoordinate } from "@/components/EdgeCrop/useEdgeCrop";
import FilterSelect from "@/components/FilterSelect";
import FilterGroup from "@/components/FilterSelect/FilterGroup";
import HBEmpty from "@/components/HBEmpty";
import HBIcon from "@/components/HBIcon";
import { useLogin } from "@/components/LoginModal";
import PinList from "@/components/PinList";
import { BuyVipDialog } from "@/components/Vip";
import { AB_SIMILAR_PAGE_LAYOUT } from "@/constants/ABMng";
import { EVENT } from "@/constants/event";
import { RENDERCARDTYPE } from "@/constants/renderCardType";
import { isBigCompanyByUser } from "@/constants/vip";
import useAppStore from "@/context/store";
import { getMasonryConf, masonryCompute } from "@/context/useWidthCompute";
import VipInfo from "@/pageComponents/MaterialHeader/vip";
import { getDeliveryDetail, getFileSimilarPins } from "@/services";
import filterUrlParams from "@/utils/filterUrlParams";
import { isGdMerged } from "@/utils/gaoding";
import { isBigCompanyByGdVip } from "@/utils/gaodingVip";
import useABTest from "@/utils/hooks/useABtest";
import { useMaterialSourceFilter } from "@/utils/hooks/useMaterialSourceFilter";
import { usePageEnterRecord } from "@/utils/hooks/usePageEnterRecord";
import { useIsMaterialVip } from "@/utils/hooks/useVip";
import imageUrl from "@/utils/imageUrl";
import { PRESETS_CONDITION } from "@/utils/steward";
import useReq from "@/utils/useReq";
import useSideNavCollapse, {
  useSideNavVisible,
} from "@/utils/useSideNavCollapse";
import { useMemoizedFn, useSetState } from "ahooks";
import { Button, Tabs } from "antd";
import classNames from "classnames";
import { isEqual, omit } from "lodash";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Else, If, Then, When } from "react-if";
import { shallow } from "zustand/shallow";
import styles from "./index.module.less";

const InspirationImageAnalysis = dynamic(
  () => import("@/components/InspirationImageAnalysis"),
  { ssr: false }
);

// 类型
enum REC_TYPE {
  pin = "pin", // 采集
  material = "material", // 素材
}

/**
 * 生成查询图缩略图 URL（用于实验组吸顶横条）
 * 使用花瓣图片服务的方图模式生成缩略图
 * @param file 文件对象 { bucket, key }
 */
function getQueryThumbnailUrl(file: {
  bucket?: string;
  key?: string;
}): string | null {
  if (!file?.bucket || !file?.key) return null;

  // 使用 imageUrl 的 sq（方图）模式生成 75x75 的缩略图
  return imageUrl(file, { width: 75, rect: "sq" });
}

/**
 * 实验组吸顶横条：缩略图 + hover 弹出框选面板
 */
function StickyThumbnailWithPopover({
  file,
  imageKey,
  imageWidth,
  imageHeight,
  placeholderContentWidth,
  onResizeEnd,
  expLeftImageRef,
}: {
  file: { bucket?: string; key?: string };
  imageKey: string;
  imageWidth: number;
  imageHeight: number;
  placeholderContentWidth: number;
  onResizeEnd: (coordinate: IRectCoordinate) => void;
  expLeftImageRef: React.RefObject<ExpLeftImageRef>;
}) {
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout>();
  const edgeCropRef = useRef<{ setCoordinate: (co: IRectCoordinate) => void }>(
    null
  );

  // hover 面板中使用固定尺寸（320px 宽度）
  const popoverWidth = 320;
  const popoverZoomRatio = popoverWidth / imageWidth;
  const popoverHeight = imageHeight * popoverZoomRatio;

  // 顶部原图使用的 zoomRatio（用于坐标转换）
  const topImageZoomRatio = placeholderContentWidth / imageWidth;

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    // 延迟隐藏，方便用户移动到弹出面板
    hideTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
    }, 150);
  };

  // 弹出面板的框选回调 - 需要转换坐标并同步顶部框选区域
  const handlePopoverResizeEnd = useMemoizedFn(
    (coordinate: IRectCoordinate) => {
      // 将弹出面板的坐标转换回顶部原图渲染坐标
      const scale = topImageZoomRatio / popoverZoomRatio;
      const originalCoordinate: IRectCoordinate = {
        left: coordinate.left * scale,
        right: coordinate.right * scale,
        top: coordinate.top * scale,
        bottom: coordinate.bottom * scale,
      };

      // 同步更新顶部原图的框选区域
      expLeftImageRef.current?.setCoordinate(originalCoordinate);

      // 触发搜索
      onResizeEnd(originalCoordinate);

      // 滚动回顶部，让用户看到原图选区变化
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  );

  // 获取当前选区并转换为 popover 坐标系
  const getCurrentPopoverCoordinate = useMemoizedFn(() => {
    const currentCoordinate = expLeftImageRef.current?.getCoordinate();
    if (!currentCoordinate) {
      return {
        left: 10,
        right: popoverWidth - 10,
        top: 10,
        bottom: Math.min(popoverHeight - 10, popoverHeight),
      };
    }

    // 将顶部原图坐标转换为 popover 坐标
    const scale = popoverZoomRatio / topImageZoomRatio;
    return {
      left: currentCoordinate.left * scale,
      right: currentCoordinate.right * scale,
      top: currentCoordinate.top * scale,
      bottom: currentCoordinate.bottom * scale,
    };
  });

  // hover 打开时同步当前选区到 popover
  useEffect(() => {
    if (isHovering && edgeCropRef.current) {
      const coordinate = getCurrentPopoverCoordinate();
      edgeCropRef.current.setCoordinate(coordinate);
    }
  }, [isHovering, getCurrentPopoverCoordinate]);

  return (
    <div
      ref={containerRef}
      className={styles.stickyThumbnailContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        className={styles.stickyThumbnail}
        src={getQueryThumbnailUrl(file)}
        alt="查询图"
      />
      {/* hover 弹出面板 */}
      {isHovering && (
        <div
          className={styles.stickyPopover}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <EdgeCrop
            ref={edgeCropRef}
            className={styles.edgeCrop}
            onResizeEnd={handlePopoverResizeEnd}
            defaultCoordinate={getCurrentPopoverCoordinate()}
          >
            <img
              src={imageUrl(file, { width: 480 })}
              width={popoverWidth}
              height={popoverHeight}
              alt="查询图"
            />
          </EdgeCrop>
        </div>
      )}
    </div>
  );
}

function SimilarEmpty() {
  return (
    <HBEmpty
      className={styles.empty}
      size="small"
      image="//cdn.dancf.com/fe-assets/img/6a5695b2efbda504b26a212d160553ec.svg"
      title="还没有采集哦~"
      subtitle={null}
    />
  );
}

function SimilarPinList({ cols, imageInfo, getPins, columnSize }) {
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);

  const queryAction = useMemo(() => {
    pageRef.current = 1;
    setHasMore(true);
    setDataSource([]);

    return async () => {
      if (!imageInfo.bucket || !imageInfo.key) {
        return;
      }

      const result = await getPins({
        limit: 20,
        page: pageRef.current,
        rec_type: REC_TYPE.pin,
        ...imageInfo,
      });

      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource((dataSource) => dataSource.concat(pins));
    };
  }, [getPins, imageInfo]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const onLoadMore = useCallback(() => {
    if (dataSource.length > 0) {
      pageRef.current = pageRef.current + 1;
      queryAction();
    }
  }, [dataSource.length, queryAction]);

  return (
    <If condition={dataSource.length > 0 || hasMore}>
      <Then>
        <PinList
          dataSource={dataSource}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          customColumnNum={cols}
          customColumnSize={columnSize}
        />
      </Then>
      <Else>
        <SimilarEmpty />
      </Else>
    </If>
  );
}

function SimilarMaterialList({
  cols,
  imageInfo,
  getPins,
  columnSize,
  filterIds,
}) {
  const router = useRouter();
  const { file_id } = router.query;
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [questionaire, setQuestionaire] = useState(null);

  // 将 URL 格式的 filter_ids 转换成接口需要的数组格式
  const filterIdsArray = useMemo(() => {
    if (!filterIds) return null;

    try {
      const filterIdMap = filterUrlParams.decode(filterIds);
      // 提取所有 values 组成数组，与搜索接口保持一致
      return Object.values(filterIdMap).reduce((result: string[], value) => {
        result.push(value as string);
        return result;
      }, []);
    } catch (e) {
      console.error("filter_ids 解析失败", e);
      return null;
    }
  }, [filterIds]);

  const queryAction = useMemo(() => {
    pageRef.current = 1;
    setHasMore(true);
    setDataSource([]);
    return async () => {
      if (!imageInfo.bucket || !imageInfo.key) {
        return;
      }

      const result = await getPins({
        limit: 40,
        page: pageRef.current,
        rec_type: REC_TYPE.material,
        ...imageInfo,
        ...(filterIdsArray ? { filter_ids: filterIdsArray } : null),
      });

      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource((dataSource) => dataSource.concat(pins));
    };
  }, [imageInfo, filterIdsArray, getPins]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  usePageEnterRecord(PRESETS_CONDITION.SIMILAR_MATERIAL_PAGE_ENTER_TIMES);

  const queryQuestionaire = useMemo(() => {
    return async () => {
      if (!file_id) {
        return;
      }
      const result = await getDeliveryDetail("web-similar-questionaire");
      setQuestionaire(
        result?.pit_modules?.[0]?.material
          ? {
              type: RENDERCARDTYPE.questionaire,
              pin_id: `questionaire-${result?.pit_modules?.[0]?.module_id}`,
              id: result?.pit_modules?.[0]?.module_id,
              name: result?.pit_modules?.[0]?.module_name,
              search_word: file_id,
              ...result?.pit_modules?.[0]?.material,
            }
          : null
      );
    };
  }, [file_id]);

  useEffect(() => {
    queryQuestionaire();
  }, [queryQuestionaire]);

  const list = useMemo(() => {
    if (dataSource.length <= 0 || !questionaire) {
      return dataSource;
    }

    const list = [...dataSource];

    if (list.length > 60 && questionaire) {
      list.splice(60, 0, questionaire);
    }

    return list;
  }, [dataSource, questionaire]);

  const onLoadMore = useCallback(() => {
    if (dataSource.length > 0) {
      pageRef.current = pageRef.current + 1;
      queryAction();
    }
  }, [dataSource, queryAction]);

  return (
    <If condition={dataSource.length > 0 || hasMore}>
      <Then>
        <PinList
          isSearch={true}
          dataSource={list}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          customColumnNum={cols}
          customColumnSize={columnSize}
        />
      </Then>
      <Else>
        <SimilarEmpty />
      </Else>
    </If>
  );
}

// ==================== 实验组组件 ====================

/**
 * 实验组：采集列表（带占位元素）
 */
function ExpSimilarPinList({
  cols,
  imageInfo,
  getPins,
  columnSize,
  placeholderCols,
  placeholderHeight,
  inspirationHeight = 68,
}) {
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);

  const queryAction = useMemo(() => {
    pageRef.current = 1;
    setHasMore(true);
    setDataSource([]);

    return async () => {
      if (!imageInfo.bucket || !imageInfo.key) {
        return;
      }

      const result = await getPins({
        limit: 20,
        page: pageRef.current,
        rec_type: REC_TYPE.pin,
        ...imageInfo,
      });

      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource((dataSource) => dataSource.concat(pins));
    };
  }, [getPins, imageInfo]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const onLoadMore = useCallback(() => {
    if (dataSource.length > 0) {
      pageRef.current = pageRef.current + 1;
      queryAction();
    }
  }, [dataSource.length, queryAction]);

  // 在数据源头部插入占位元素（始终插入，确保骨架屏位置正确）
  // 左侧列：原图高度占位；右侧列：灵感发散高度占位
  // 注意：右侧占位元素的 pin_id 包含 inspirationHeight，确保高度变化时 React 重新渲染
  const dataWithPlaceholders = useMemo(() => {
    const rightPlaceholderCols = cols - placeholderCols;

    // 左侧列占位（原图高度）
    const leftPlaceholders = Array.from(
      { length: placeholderCols },
      (_, i) => ({
        pin_id: `placeholder-left-${i}`,
        _isPlaceholder: true,
        _placeholderHeight: placeholderHeight,
      })
    );

    // 右侧列占位（灵感发散高度，使用动态值）
    // pin_id 包含 inspirationHeight，确保高度变化时被识别为新元素
    const rightPlaceholders = Array.from(
      { length: rightPlaceholderCols },
      (_, i) => ({
        pin_id: `placeholder-right-${i}-h${Math.round(inspirationHeight)}`,
        _isPlaceholder: true,
        _placeholderHeight: inspirationHeight,
      })
    );

    return [...leftPlaceholders, ...rightPlaceholders, ...dataSource];
  }, [dataSource, cols, placeholderCols, placeholderHeight, inspirationHeight]);

  // 灵感发散高度变化时，触发瀑布流重排
  useEffect(() => {
    window.dispatchEvent(new CustomEvent(EVENT.MASONRY_RELAYOUT));
  }, [inspirationHeight]);

  return (
    <If condition={dataSource.length > 0 || hasMore}>
      <Then>
        <PinList
          dataSource={dataWithPlaceholders}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          customColumnNum={cols}
          customColumnSize={columnSize}
          renderPinCard={(record, index, defaultRender) => {
            // 占位元素渲染为透明 div
            if (record._isPlaceholder) {
              return (
                <div
                  key={record.pin_id}
                  className={styles.placeholder}
                  style={{ height: record._placeholderHeight }}
                />
              );
            }
            // 正常卡片使用默认渲染
            return defaultRender();
          }}
        />
      </Then>
      <Else>
        <SimilarEmpty />
      </Else>
    </If>
  );
}

/**
 * 实验组：素材列表（带占位元素）
 */
function ExpSimilarMaterialList({
  cols,
  imageInfo,
  getPins,
  columnSize,
  filterIds,
  placeholderCols,
  placeholderHeight,
}) {
  const router = useRouter();
  const { file_id } = router.query;
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [questionaire, setQuestionaire] = useState(null);

  // 将 URL 格式的 filter_ids 转换成接口需要的数组格式
  const filterIdsArray = useMemo(() => {
    if (!filterIds) return null;

    try {
      const filterIdMap = filterUrlParams.decode(filterIds);
      return Object.values(filterIdMap).reduce((result: string[], value) => {
        result.push(value as string);
        return result;
      }, []);
    } catch (e) {
      console.error("filter_ids 解析失败", e);
      return null;
    }
  }, [filterIds]);

  const queryAction = useMemo(() => {
    pageRef.current = 1;
    setHasMore(true);
    setDataSource([]);
    return async () => {
      if (!imageInfo.bucket || !imageInfo.key) {
        return;
      }

      const result = await getPins({
        limit: 40,
        page: pageRef.current,
        rec_type: REC_TYPE.material,
        ...imageInfo,
        ...(filterIdsArray ? { filter_ids: filterIdsArray } : null),
      });

      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource((dataSource) => dataSource.concat(pins));
    };
  }, [imageInfo, filterIdsArray, getPins]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  usePageEnterRecord(PRESETS_CONDITION.SIMILAR_MATERIAL_PAGE_ENTER_TIMES);

  const queryQuestionaire = useMemo(() => {
    return async () => {
      if (!file_id) {
        return;
      }
      const result = await getDeliveryDetail("web-similar-questionaire");
      setQuestionaire(
        result?.pit_modules?.[0]?.material
          ? {
              type: RENDERCARDTYPE.questionaire,
              pin_id: `questionaire-${result?.pit_modules?.[0]?.module_id}`,
              id: result?.pit_modules?.[0]?.module_id,
              name: result?.pit_modules?.[0]?.module_name,
              search_word: file_id,
              ...result?.pit_modules?.[0]?.material,
            }
          : null
      );
    };
  }, [file_id]);

  useEffect(() => {
    queryQuestionaire();
  }, [queryQuestionaire]);

  const list = useMemo(() => {
    if (dataSource.length <= 0 || !questionaire) {
      return dataSource;
    }

    const listCopy = [...dataSource];

    if (listCopy.length > 60 && questionaire) {
      listCopy.splice(60, 0, questionaire);
    }

    return listCopy;
  }, [dataSource, questionaire]);

  const onLoadMore = useCallback(() => {
    if (dataSource.length > 0) {
      pageRef.current = pageRef.current + 1;
      queryAction();
    }
  }, [dataSource, queryAction]);

  // 在数据源头部插入占位元素（始终插入，确保骨架屏位置正确）
  const dataWithPlaceholders = useMemo(() => {
    const placeholders = Array.from({ length: placeholderCols }, (_, i) => ({
      pin_id: `placeholder-${i}`,
      _isPlaceholder: true,
      _placeholderHeight: placeholderHeight,
    }));
    return [...placeholders, ...list];
  }, [list, placeholderCols, placeholderHeight]);

  return (
    <If condition={dataSource.length > 0 || hasMore}>
      <Then>
        <PinList
          isSearch={true}
          dataSource={dataWithPlaceholders}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          customColumnNum={cols}
          customColumnSize={columnSize}
          renderPinCard={(record, index, defaultRender) => {
            // 占位元素渲染为透明 div
            if (record._isPlaceholder) {
              return (
                <div
                  key={record.pin_id}
                  className={styles.placeholder}
                  style={{ height: record._placeholderHeight }}
                />
              );
            }
            // 正常卡片使用默认渲染
            return defaultRender();
          }}
        />
      </Then>
      <Else>
        <SimilarEmpty />
      </Else>
    </If>
  );
}

export default function SimilarPage() {
  const router = useRouter();
  const {
    backUrl,
    bucket,
    key,
    file_id,
    type = "pins",
    width: imageWidth,
    height: imageHeight,
    filter_ids,
  } = router.query;

  // AB 实验：图搜页面布局
  const layoutAB = useABTest(AB_SIMILAR_PAGE_LAYOUT);

  // 进入相似页面时清空 pinStack，确保弹窗返回逻辑正确
  useEffect(() => {
    const { clearPinStack, pinStack } = useAppStore.getState();
    if (pinStack && pinStack.length > 0) {
      clearPinStack();
    }
  }, []);
  const {
    headerHeight: height,
    userReady,
    user,
    vips,
    wrapperWidth,
  } = useAppStore(
    ({ headerHeight, userReady, user, vips, wrapperWidth }) => ({
      headerHeight,
      userReady,
      user,
      vips,
      wrapperWidth,
    }),
    shallow
  );
  const sideNavVisible = useSideNavVisible();
  const [isCollapsed] = useSideNavCollapse();
  const { login } = useLogin();

  const [showHeader, setShowHeader] = useState(false);

  const headerRef = useRef(null);

  // 使用素材来源筛选器 Hook
  const {
    filter: materialSourceFilter,
    onFilterChange,
    getFilterIdsForOption,
  } = useMaterialSourceFilter();

  useEffect(() => {
    const onScroll = () => {
      const rect = headerRef.current?.getBoundingClientRect();
      setShowHeader(rect?.bottom - height <= 0);
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [height]);

  const buyVip = useCallback(async () => {
    if (user) {
      BuyVipDialog.open();
    } else {
      const res = await login({
        source_location: "相似素材-购买会员",
      });
      if (!res) {
        return;
      }
      BuyVipDialog.open();
    }
  }, [login, user]);

  const isVip = useIsMaterialVip();

  const onTabChange = useCallback(
    (activeKey) => {
      router.push({
        pathname: `/similar${activeKey === "pins" ? "" : "/" + activeKey}`,
        query: {
          ...omit(router.query, "type"),
        },
      });
    },
    [router]
  );

  const isBigCompany = useMemo(() => {
    return isGdMerged(user)
      ? isBigCompanyByGdVip(vips as IGdVip[])
      : isBigCompanyByUser(user);
  }, [user, vips]);

  const file = useMemo(() => {
    if (bucket && key) {
      return {
        bucket: Array.isArray(bucket) ? bucket[0] : bucket,
        key: Array.isArray(key) ? key[0] : key,
        file_id: Array.isArray(file_id) ? file_id[0] : file_id,
      };
    }
  }, [bucket, file_id, key]);

  const [pageStyle, setPageStyle] = useState<{
    rightWidth: number;
    cols: number;
    pageWidth: number;
    leftHeight: number;
    columnSize: number;
  }>(null);

  // 实验组：全宽瀑布流配置
  const [expPageStyle, setExpPageStyle] = useState<{
    cols: number;
    columnSize: number;
    pageWidth: number;
    placeholderCols: number;
    placeholderWidth: number; // 含最后间距，用于灵感发散定位
    placeholderContentWidth: number; // 不含最后间距，用于原图居中
    inspirationWidth: number;
  }>(null);

  // 计算右边瀑布流列数、页面宽度
  useEffect(() => {
    function calculateWidth() {
      const leftWidth = 480 + 24 + 16;
      const leftHeight = window.innerHeight - height - 88 - 60;

      const config = getMasonryConf();

      // 对照组：排除左侧宽度
      const result = masonryCompute({
        containerWidth: wrapperWidth - leftWidth + 24 - config.margin,
        ...config,
      });

      setPageStyle({
        rightWidth:
          result.cols * result.columnSize + config.itemGap * (result.cols - 1),
        cols: result.cols,
        pageWidth: wrapperWidth,
        leftHeight,
        columnSize: result.columnSize,
      });

      // 实验组：使用全宽计算
      const expResult = masonryCompute({
        containerWidth: wrapperWidth - config.margin,
        ...config,
      });

      // 计算占位列数：原图宽度(480) + 间距(24) 需要占据几列
      const imageAreaWidth = 480 + 24;
      const placeholderCols = Math.ceil(
        imageAreaWidth / (expResult.columnSize + config.itemGap)
      );

      // 计算占位区域的实际宽度（用于灵感发散的左边距，包含右侧间距）
      const placeholderWidth =
        placeholderCols * expResult.columnSize +
        config.itemGap * placeholderCols;

      // 计算占位区域的内容宽度（不含最后间距，用于原图居中对齐）
      const placeholderContentWidth =
        placeholderCols * expResult.columnSize +
        config.itemGap * (placeholderCols - 1);

      // 计算灵感发散区域宽度（非占位区域，与对照组右侧宽度一致）
      const rightCols = expResult.cols - placeholderCols;
      const inspirationWidth =
        rightCols * expResult.columnSize + config.itemGap * (rightCols - 1);

      setExpPageStyle({
        cols: expResult.cols,
        columnSize: expResult.columnSize,
        pageWidth: wrapperWidth,
        placeholderCols,
        placeholderWidth,
        placeholderContentWidth,
        inspirationWidth,
      });
    }

    calculateWidth();

    window.addEventListener("resize", calculateWidth);
    return () => {
      window.removeEventListener("resize", calculateWidth);
    };
  }, [height, isCollapsed, sideNavVisible, wrapperWidth]);

  // 计算图片渲染信息（对照组）
  const { renderHeight, renderWidth, zoomRatio } = useMemo(() => {
    const minHeight = 340;

    const height = +imageHeight;
    const width = +imageWidth;

    // 如果图片转成480宽度后，小于最小展示图片高度，则需要出现横向滚动条
    if (width / height > 4 && (height / width) * 480 < minHeight) {
      return {
        renderHeight: minHeight,
        renderWidth: (width / height) * minHeight,
        /**缩放比例 */
        zoomRatio: minHeight / height,
      };
    }

    return {
      renderWidth: 480,
      renderHeight: (height / width) * 480,
      /**缩放比例 */
      zoomRatio: 480 / width || 1,
    };
  }, [imageHeight, imageWidth]);

  // 实验组：灵感发散组件高度状态
  const [inspirationHeight, setInspirationHeight] = useState(68);

  // 实验组：顶部原图 EdgeCrop 的 ref，用于同步框选区域
  const expLeftImageRef = useRef<ExpLeftImageRef>(null);

  // 实验组：使用 ResizeObserver 监听灵感发散组件高度变化
  const resizeObserverRef = useRef<ResizeObserver>(null);

  // 使用 callback ref 确保 ResizeObserver 在元素挂载时正确设置
  const inspirationRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      // 清理之前的 observer
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      // 如果不是实验组或节点不存在，直接返回
      if (!layoutAB.new || !node) return;

      // 创建新的 ResizeObserver
      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          // 更新高度（包括收起时的小高度）
          setInspirationHeight(height > 0 ? height : 68);
        }
      });

      resizeObserverRef.current.observe(node);
    },
    [layoutAB.new]
  );

  // 组件卸载时清理 ResizeObserver
  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  // 以图搜图信息：图片 + 搜索范围
  const [imageInfo, setImageInfo] = useSetState(null);

  const onResizeEnd = useMemoizedFn((coordinate: IRectCoordinate) => {
    // 实验组使用 placeholderContentWidth 计算的 zoomRatio，对照组使用 zoomRatio
    const currentZoomRatio =
      layoutAB.new && expPageStyle
        ? expPageStyle.placeholderContentWidth / +imageWidth
        : zoomRatio;

    const nextImageInfo = {
      // 图片信息
      bucket,
      key,
      file_id,
      // 搜索范围
      x: Math.floor(coordinate.left / currentZoomRatio),
      y: Math.floor(coordinate.top / currentZoomRatio),
      h: Math.floor((coordinate.bottom - coordinate.top) / currentZoomRatio),
      w: Math.floor((coordinate.right - coordinate.left) / currentZoomRatio),
    };

    if (!isEqual(nextImageInfo, imageInfo)) {
      setImageInfo(nextImageInfo);
    }
  });

  const [err, setErr] = useState(null);
  const { runAsync: getPins } = useReq(
    async (query) => {
      const res = await getFileSimilarPins(query);
      if (res.err === 403) {
        setErr(true);
      }
      return res;
    },
    {
      manual: true,
    }
  );

  return (
    <div
      className={classNames(styles.similar)}
      data-content-source="信息流"
      style={{
        width: pageStyle?.pageWidth,
        // @ts-ignore
        "--column-size": `${pageStyle?.columnSize}px`,
      }}
    >
      <Head>
        <title>相似图片-花瓣网</title>
      </Head>
      <div
        className={classNames([
          styles.sticky,
          showHeader ? styles.show : null,
          "modalOpenWidth",
        ])}
        style={{
          top: `${height}px`,
        }}
      >
        <div
          className={classNames(styles.wrapper)}
          style={{ width: pageStyle?.pageWidth }}
        >
          <div className={styles.left}>
            <When condition={!!backUrl}>
              <Button
                className={styles.button}
                type="link"
                onClick={() => router.push(backUrl as string)}
                icon={
                  <HBIcon className={styles.icon} type="ic_arrowThinLeft" />
                }
              />
            </When>
          </div>
          <div className={styles.middle}>
            {/* 实验组：显示查询图缩略图，hover 显示框选面板 */}
            {layoutAB.new && file && expPageStyle && (
              <StickyThumbnailWithPopover
                file={file}
                imageKey={key as string}
                imageWidth={+imageWidth}
                imageHeight={+imageHeight}
                placeholderContentWidth={expPageStyle.placeholderContentWidth}
                onResizeEnd={onResizeEnd}
                expLeftImageRef={expLeftImageRef}
              />
            )}
            <div className={styles.title}>相似图片</div>
          </div>
          <div className={styles.right}>
            <When condition={userReady && !isVip && type === "materials"}>
              <div className={styles.buyVip}>
                <Button type="primary" onClick={() => buyVip()}>
                  开通花瓣素材VIP
                </Button>
              </div>
            </When>
          </div>
        </div>
      </div>
      <div className={styles.header} ref={headerRef}>
        <div className={styles.back}>
          <When condition={!!backUrl}>
            <Button
              className={styles.button}
              type="link"
              onClick={() => router.push(backUrl as string)}
              icon={<HBIcon className={styles.icon} type="ic_arrowThinLeft" />}
            />
          </When>
        </div>
        <div className={styles.info}>
          <div className={styles.title}>相似图片</div>

          {type === "materials" && (
            <div className={styles.right}>
              <VipInfo />
            </div>
          )}
        </div>
      </div>
      <Tabs
        className={styles.tab}
        destroyInactiveTabPane={true}
        activeKey={type as string}
        onChange={onTabChange}
        tabBarExtraContent={
          type === "materials" && materialSourceFilter ? (
            <div className={styles.filterBar}>
              <FilterGroup>
                <FilterSelect
                  name={materialSourceFilter.name}
                  options={materialSourceFilter.options}
                  value={materialSourceFilter.value}
                  onChange={onFilterChange}
                  getPopupContainer={() => document.body}
                  getOptionExtData={(optionValue) => ({
                    filter_ids: getFilterIdsForOption(optionValue),
                  })}
                />
              </FilterGroup>
            </div>
          ) : null
        }
      >
        <Tabs.TabPane
          tab={
            <div data-gd-click="button_click" data-button-name="相似采集">
              相似采集
            </div>
          }
          key="pins"
        ></Tabs.TabPane>
        {!isBigCompany ? (
          <Tabs.TabPane
            tab={
              <div
                className={styles.title}
                data-gd-click="button_click"
                data-button-name="相似商用素材"
              >
                <div className={styles.text}>相似商用素材</div>
                <img
                  className={styles.label}
                  alt="源文件"
                  src="//cdn.dancf.com/fe-assets/20230712/b19d4a40eedd9f44ec0a265c1b651133.png"
                />
              </div>
            }
            key="materials"
          ></Tabs.TabPane>
        ) : null}
      </Tabs>
      {err ? (
        <div className={styles.err}>
          <HBEmpty
            image="https://grocery-cdn.huaban.com/file/aeb481b0b08c11ef911b1f4815c133e0.svg"
            title="该图片内容可能违规"
            subtitle={
              <>
                你可以
                <a
                  className="primary-text-btn"
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent(EVENT.SEARCH_SIMILAR_IMG)
                    )
                  }
                >
                  换一张图片
                </a>
                试试
              </>
            }
          />
        </div>
      ) : layoutAB.new && expPageStyle && imageWidth && imageHeight ? (
        // 实验组布局：原图下方展示瀑布流，整体滚动
        <div className={styles.contentExp}>
          {/* 原图浮动层：左对齐，宽度与占位瀑布流列对齐，高度自适应 */}
          <div
            className={styles.imageFloating}
            style={{
              // 宽度与占位列对齐（不含最后间距）
              width: expPageStyle.placeholderContentWidth,
              // 左对齐
              left: 0,
            }}
          >
            <ExpLeftImage
              ref={expLeftImageRef}
              file={file}
              imageKey={key as string}
              // 使用占位列宽度作为渲染宽度，高度按比例计算
              renderWidth={expPageStyle.placeholderContentWidth}
              renderHeight={
                (+imageHeight / +imageWidth) *
                expPageStyle.placeholderContentWidth
              }
              onResizeEnd={onResizeEnd}
            />
          </div>
          {/* 灵感发散：绝对定位在原图右侧 */}
          {type === "pins" && (
            <div
              ref={inspirationRefCallback}
              className={styles.inspirationFloating}
              style={{
                left: expPageStyle.placeholderWidth,
                width: expPageStyle.inspirationWidth,
              }}
            >
              <InspirationImageAnalysis file={file} />
            </div>
          )}
          {/* 瀑布流 */}
          {imageInfo && (
            <div className={styles.pinListFull}>
              {type === "pins" ? (
                <ExpSimilarPinList
                  cols={expPageStyle.cols}
                  imageInfo={imageInfo}
                  getPins={getPins}
                  columnSize={expPageStyle.columnSize}
                  placeholderCols={expPageStyle.placeholderCols}
                  placeholderHeight={
                    (+imageHeight / +imageWidth) *
                      expPageStyle.placeholderContentWidth +
                    24
                  }
                  inspirationHeight={inspirationHeight}
                />
              ) : (
                <ExpSimilarMaterialList
                  cols={expPageStyle.cols}
                  imageInfo={imageInfo}
                  getPins={getPins}
                  columnSize={expPageStyle.columnSize}
                  filterIds={filter_ids as string}
                  placeholderCols={expPageStyle.placeholderCols}
                  placeholderHeight={
                    (+imageHeight / +imageWidth) *
                      expPageStyle.placeholderContentWidth +
                    24
                  }
                />
              )}
            </div>
          )}
        </div>
      ) : (
        // 对照组布局：左右分栏，左侧固定
        pageStyle && (
          <div className={styles.content}>
            <LeftImage
              file={file}
              imageKey={key}
              headerHeight={height}
              maxHeight={pageStyle.leftHeight}
              renderHeight={renderHeight}
              renderWidth={renderWidth}
              onResizeEnd={onResizeEnd}
            />
            {imageInfo && (
              <div
                className={styles.right}
                style={{ width: pageStyle.rightWidth }}
              >
                {type === "pins" ? (
                  <>
                    <InspirationImageAnalysis file={file} />
                    <SimilarPinList
                      cols={pageStyle.cols}
                      imageInfo={imageInfo}
                      getPins={getPins}
                      columnSize={pageStyle.columnSize}
                    />
                  </>
                ) : (
                  <SimilarMaterialList
                    cols={pageStyle.cols}
                    imageInfo={imageInfo}
                    getPins={getPins}
                    columnSize={pageStyle.columnSize}
                    filterIds={filter_ids as string}
                  />
                )}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

function LeftImage({
  file,
  imageKey,
  headerHeight,
  maxHeight,
  renderHeight,
  renderWidth,
  onResizeEnd,
}) {
  const router = useRouter();
  const getDefaultCoordinate = useMemoizedFn(() => {
    if (!router.query?.height) {
      return null;
    }
    return {
      left: 10,
      right: renderWidth - 10,
      top: 10,
      bottom: renderHeight - 10,
    };
  });

  const edgeCropRef = useRef<{
    setCoordinate: (co: IRectCoordinate) => void;
  }>(null);

  // 图片更新时，重新设置默认框选范围
  useEffect(() => {
    const defaultCoordinate = getDefaultCoordinate();
    if (defaultCoordinate) {
      edgeCropRef.current?.setCoordinate(defaultCoordinate);
      onResizeEnd?.(defaultCoordinate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageKey]);

  return (
    <div className={styles.left} style={{ top: 88 + headerHeight, maxHeight }}>
      <EdgeCrop
        ref={edgeCropRef}
        className={styles.edgeCrop}
        onResizeEnd={onResizeEnd}
        defaultCoordinate={getDefaultCoordinate()}
      >
        <img
          src={imageUrl(
            file,
            renderWidth === 480
              ? { width: 480 }
              : {
                  origin: true,
                }
          )}
          width={renderWidth}
          height={renderHeight}
          alt="原图"
        />
      </EdgeCrop>
    </div>
  );
}

/**
 * 实验组：左侧原图（不固定，跟随页面滚动）
 * 暴露 setCoordinate/getCoordinate 方法用于同步框选区域
 */
export interface ExpLeftImageRef {
  setCoordinate: (co: IRectCoordinate) => void;
  getCoordinate: () => IRectCoordinate | null;
}

const ExpLeftImage = forwardRef<
  ExpLeftImageRef,
  {
    file: { bucket?: string; key?: string };
    imageKey: string;
    renderHeight: number;
    renderWidth: number;
    onResizeEnd: (coordinate: IRectCoordinate) => void;
  }
>(function ExpLeftImage(
  { file, imageKey, renderHeight, renderWidth, onResizeEnd },
  ref
) {
  const router = useRouter();
  const getDefaultCoordinate = useMemoizedFn(() => {
    if (!router.query?.height) {
      return null;
    }
    return {
      left: 10,
      right: renderWidth - 10,
      top: 10,
      bottom: renderHeight - 10,
    };
  });

  const edgeCropRef = useRef<{
    setCoordinate: (co: IRectCoordinate) => void;
  }>(null);

  // 追踪当前选区坐标和前一次渲染尺寸
  const currentCoordinateRef = useRef<IRectCoordinate>(null);
  const prevSizeRef = useRef<{ width: number; height: number }>(null);

  // 暴露 setCoordinate/getCoordinate 方法给父组件
  useImperativeHandle(ref, () => ({
    setCoordinate: (co: IRectCoordinate) => {
      currentCoordinateRef.current = co;
      edgeCropRef.current?.setCoordinate(co);
    },
    getCoordinate: () => currentCoordinateRef.current,
  }));

  // 包装 onResizeEnd，同时更新当前选区引用
  const handleResizeEnd = useMemoizedFn((coordinate: IRectCoordinate) => {
    currentCoordinateRef.current = coordinate;
    onResizeEnd?.(coordinate);
  });

  // 图片更新时，重新设置默认框选范围
  useEffect(() => {
    const defaultCoordinate = getDefaultCoordinate();
    if (defaultCoordinate) {
      currentCoordinateRef.current = defaultCoordinate;
      prevSizeRef.current = { width: renderWidth, height: renderHeight };
      edgeCropRef.current?.setCoordinate(defaultCoordinate);
      onResizeEnd?.(defaultCoordinate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageKey]);

  // 渲染尺寸变化时，按比例缩放选区
  useEffect(() => {
    const prevSize = prevSizeRef.current;
    const currentCoordinate = currentCoordinateRef.current;

    // 首次渲染或没有选区时，记录当前尺寸并跳过
    if (!prevSize || !currentCoordinate) {
      prevSizeRef.current = { width: renderWidth, height: renderHeight };
      return;
    }

    // 尺寸没有变化时跳过
    if (prevSize.width === renderWidth && prevSize.height === renderHeight) {
      return;
    }

    // 计算缩放比例
    const scaleX = renderWidth / prevSize.width;
    const scaleY = renderHeight / prevSize.height;

    // 按比例缩放选区坐标
    const scaledCoordinate: IRectCoordinate = {
      left: currentCoordinate.left * scaleX,
      right: currentCoordinate.right * scaleX,
      top: currentCoordinate.top * scaleY,
      bottom: currentCoordinate.bottom * scaleY,
    };

    // 更新选区和记录
    currentCoordinateRef.current = scaledCoordinate;
    prevSizeRef.current = { width: renderWidth, height: renderHeight };
    edgeCropRef.current?.setCoordinate(scaledCoordinate);
    onResizeEnd?.(scaledCoordinate);
  }, [renderWidth, renderHeight, onResizeEnd]);

  return (
    <EdgeCrop
      ref={edgeCropRef}
      className={styles.edgeCrop}
      onResizeEnd={handleResizeEnd}
      defaultCoordinate={getDefaultCoordinate()}
    >
      <img
        src={imageUrl(
          file,
          renderWidth === 480
            ? { width: 480 }
            : {
                origin: true,
              }
        )}
        width={renderWidth}
        height={renderHeight}
        alt="原图"
      />
    </EdgeCrop>
  );
});
